import { formatFileSize } from './fileSize.ts';

/**
 * Maximum attachments (images + files combined) ingested from a single inbound
 * message; extras are dropped. Equal to how many files one turn may load into
 * context, so a full message can be read whole. Shared so the webhook cap, the
 * model-facing note, and the UI label all agree.
 */
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;

/**
 * Chat files (not images) larger than this are rejected, before and after an
 * Office file is converted to PDF. Shared so the browser refuses an oversized
 * file for the same reason the messaging channels do.
 */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;

/** Chat images larger than this are rejected, on every channel. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/**
 * Documents kept on a meeting conversation have their own, smaller cap: each
 * one is read and chunked into a preparation.
 */
export const MAX_MEETING_DOCUMENT_BYTES = 5 * 1024 * 1024;

/**
 * What a file picked in the app's chat may be. Images, PDFs and plain text
 * reach the model as they are; Word and PowerPoint documents are converted to
 * PDF on the way in, the same as when they arrive over Teams or Slack.
 *
 * Stated here so the file picker, the pre-upload check and the backend's own
 * validation cannot drift apart.
 */
export const CHAT_UPLOAD_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
] as const;

export type ChatUploadMime = (typeof CHAT_UPLOAD_MIME_TYPES)[number];

export function isChatUploadMime(mimeType: string): mimeType is ChatUploadMime {
  return (CHAT_UPLOAD_MIME_TYPES as readonly string[]).includes(mimeType);
}

/**
 * The subject phrase for a group of unavailable attachments: their quoted names
 * when we have a name for every one (so the list never disagrees with the
 * count), otherwise a plain count like "3 files" — or "3 more files" when `more`
 * is set, for the too-many case.
 */
export function unavailableSubject(
  count: number,
  fileNames: string[] | undefined,
  more = false,
): string {
  const noun = count === 1 ? 'file' : 'files';
  const named =
    fileNames?.length === count ? fileNames.map((name) => `"${name}"`).join(', ') : '';
  return named || `${count}${more ? ' more' : ''} ${noun}`;
}

/**
 * Comma-joined human-readable sizes, but only when we have a size for every file
 * — so a size never maps to the wrong file. Empty string otherwise.
 */
export function formattedFileSizes(sizes: number[] | undefined, count: number): string {
  return sizes?.length === count ? sizes.map(formatFileSize).join(', ') : '';
}
